import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { startOfWeek, type ISODate } from '@app/shared'

import { Check, Info, Pencil } from '../icons'
// The "+ tag" chip, shared verbatim with the day page so the two surfaces cannot drift about what
// a tag is. Its own import line — parallel lanes edit this file's import block.
import { AddTagChip } from '../journal/AddTagChip'
import { VoiceRecorder } from './VoiceRecorder'
import { clockFromTimestamp, durationPhrase } from '../../lib/time'
import { useJournalPrefs } from '../../lib/journal-prefs'
// The durable journal draft (JRN-1a/b), as the hook every composer in the app now shares — a
// browser tab crashing or a session expiring loses words exactly as fast here, and the desktop
// composer must not be the lossy one. Its own import line, by this file's convention.
import { unsavedSentence, useJournalDraft } from '../../lib/use-journal-draft'
import {
  useJournalEntries,
  useJournalEntry,
  useSaveJournalEntry,
  useVoiceMemos,
  type JournalVoiceMemo,
} from '../../lib/data'

type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

/**
 * Long enough that a fast typist is not sending a request per word, short enough that leaving the
 * page mid-thought loses nothing (the pending write is flushed on the way out, below).
 */
const AUTOSAVE_MS = 700

/**
 * THE COMPOSER — docs/design/handoff/"LifeOS Home Options.dc.html" option 3c, and the home of the
 * whole product. Everything else on the screen is context around it.
 *
 * THE UNDO CONTRACT — no gate can see this, so it is written down here.
 * The textarea is UNCONTROLLED (defaultValue, never value). A controlled input whose value is
 * re-set by an autosave round trip silently destroys the browser's native undo stack after the
 * first save, and no screenshot and no assertion can photograph a broken Ctrl+Z. The `key` on the
 * textarea makes React remount it when the DATE changes, which is the only time the text should
 * be replaced from outside.
 * If you ever change this to a controlled input, check by hand: type, wait for the save chip,
 * press Ctrl+Z.
 */
export function JournalEntryCard({ date, nowMinutes }: { date: ISODate; nowMinutes: number }) {
  const { data: entry, isPending, error: readError, refetch } = useJournalEntry(date)
  const save = useSaveJournalEntry(date)
  // Settings → Journal defaults. Browser-local, and they hide CHROME only: a hidden prompt is
  // still on the entry and still rendered on the day page, a hidden mood row still has whatever
  // mood was recorded. Nothing here writes.
  const prefs = useJournalPrefs()

  const [words, setWords] = useState(0)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * The write that is waiting on the debounce, or null when nothing is pending. It carries its own
   * date: a flush can happen after the day has rolled over, and writing today's text onto
   * yesterday's page would be worse than losing it.
   */
  const pending = useRef<{ date: ISODate; text: string } | null>(null)

  // ── THE DURABLE DRAFT ─────────────────────────────────────────
  //
  // Every rule about where unsaved words live is in lib/use-journal-draft.ts, shared with the
  // phone composer and the day page. What stays here is this card's debounce and its pixels.
  //
  // `readFailed` is not passed: this card HIDES the field while the read is failing (below), so
  // there is no way to type blind on it and nothing for the draft-only mode to protect.
  const draft = useJournalDraft({
    date,
    serverText: entry?.text,
    settled: !isPending && !readError,
    busy: save.isPending || pending.current !== null,
    send: (text) => commit(text),
  })
  const { restored, unconfirmed, kept, autoRetries } = draft
  /** The box is ahead of the server, read during render so the word-count effect can see it. */
  const ahead = useRef(false)
  ahead.current = unconfirmed

  useEffect(() => {
    // Held back while a draft is in the box: a count taken from the server's text next to words
    // the server has not seen is the card disagreeing with itself.
    if (entry && !ahead.current) setWords(entry.wordCount)
  }, [entry?.date, entry?.wordCount, entry])

  // The restored words are the ones on screen, so they are the ones counted.
  useEffect(() => {
    if (restored) setWords(restored.text.trim() ? restored.text.trim().split(/\s+/).length : 0)
  }, [restored])

  // The mutation is reached through a ref so the flush effect below can depend on nothing and
  // therefore never tear down and rebuild its listeners mid-sentence.
  const flushRef = useRef<() => void>(() => {})
  flushRef.current = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const write = pending.current
    if (!write) return
    pending.current = null
    save.mutate(write, {
      onSuccess: () => setSaveState('saved'),
      onError: () => setSaveState('failed'),
    })
  }

  useEffect(() => {
    // THE KEYSTROKE PROMISE. A debounce is a window in which text exists only in the DOM. Leaving
    // the screen, closing the tab, or switching apps on the phone all land inside that window
    // eventually — so each of them flushes the pending write instead of dropping the timer.
    // `pagehide` rather than `beforeunload`: iOS/Android background a tab without ever firing
    // `beforeunload`, and this is a phone-first app.
    const flush = () => flushRef.current()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', flush)
      flush() // unmounting (a nav click, a date change) counts as leaving too
    }
  }, [])

  /** One save, with the chip told the truth afterwards. The draft is cleared by the mutation. */
  function commit(text: string) {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    pending.current = null
    setSaveState('saving')
    save.mutate(
      { date, text },
      {
        onSuccess: (saved) => {
          draft.noteSaved(saved.text)
          setSaveState('saved')
        },
        // The text is still in the textarea — it is uncontrolled, so a failed save cannot erase
        // it, and since JRN-1a it is on disk as well. What must not happen is the card claiming
        // "Saved" when nothing was.
        onError: () => setSaveState('failed'),
      },
    )
  }

  function onType(text: string) {
    setWords(text.trim() ? text.trim().split(/\s+/).length : 0)
    // DURABLE FIRST, AND SYNCHRONOUSLY — before the debounce and before the request, because
    // everything after this line can be interrupted and this line cannot.
    draft.noteKeystroke(text)
    setSaveState('saving')
    pending.current = { date, text }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      pending.current = null
      commit(text)
    }, AUTOSAVE_MS)
  }

  return (
    // Radius and shadow from the README's component spec (radius 16, 1px line, shadow-card).
    // The rendered mockup draws this card at radius 18 with a 0 6px 24px shadow; the README is
    // canonical for radii and shadows, so the README wins and the 2px is noted in the report.
    // 12d, TEXT ENTRY — the third of the brief's three fields. It keeps the YELLOW LEFT RULE
    // (a 3px inset in the `journal` hue) on hover and adds the matching halo on focus, rather than
    // taking the neutral fill the search pill and quick-add row take. The brief's stated reason is the shape
    // rule ("because it's a radius-10 surface"); this card is radius 16 by the README's component
    // spec, which the FIDELITY internal-precedence rule makes canonical for radii. So the SHAPE
    // argument does not apply here but the NAMED INSTRUCTION does — the brief calls the journal
    // composer out by name and by hue. Flagged in the port report: the two documents disagree
    // about this card's radius, not about its motion.
    <section
      style={{ '--mo-rest-shadow': 'var(--shadow-card)' } as CSSProperties}
      className="mo-field-journal flex flex-none gap-[30px] rounded-context border border-line bg-surface px-[30px] pb-5 pt-6 shadow-card"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-none items-center gap-[10px]">
          <Pencil size={19} strokeWidth={1.7} className="text-journal-deep" />
          <span className="text-[11px] font-semibold leading-none tracking-[0.06em] text-muted">
            {prefs.dailyPrompt ? 'DAILY PROMPT' : 'TODAY'}
          </span>
          <SaveChip
            state={saveState}
            unconfirmed={unconfirmed}
            kept={kept}
            autoRetries={autoRetries}
            lastSavedAt={entry && entry.text.length > 0 ? entry.updatedAt : null}
            date={date}
            nowMinutes={nowMinutes}
          />
          {prefs.dailyPrompt && <WhyThisPrompt />}
        </header>

        {/* The prompt while the read is in flight, then whatever prompt the day actually has.
            An empty prompt renders nothing rather than a permanent ellipsis: the rotating prompt
            pool is V6, so a real entry with no prompt is the normal case, not a stuck loader.
            Turned off entirely on Settings → Journal defaults, which hides the heading and its
            skeleton — the entry keeps its prompt and the day page still shows it. */}
        {!prefs.dailyPrompt ? null : isPending ? (
          <div className="mt-4 h-[40px] max-w-[560px] animate-pulse rounded-tile bg-fill" aria-hidden />
        ) : entry?.prompt ? (
          <h1 className="mt-4 max-w-[920px] text-[33px] font-light leading-[1.22] tracking-[-0.025em] text-pretty">
            {entry.prompt}
          </h1>
        ) : null}

        {/*
          A FAILED READ MUST NOT LOOK LIKE AN EMPTY DAY. If today's entry could not be fetched,
          an empty textarea says "you wrote nothing" — and the first keystroke would then
          overwrite whatever is actually on the server with a blank. So the read failure is
          stated, writing is held until it succeeds, and there is exactly one action.
        */}
        {readError ? (
          <div className="mt-[14px] max-w-[620px] rounded-row border border-danger-line bg-danger-fill px-4 py-3">
            <p className="text-body-sm text-danger text-pretty">
              Today’s entry could not be loaded, so it is not shown and writing is held — typing
              into a blank box would overwrite whatever is saved. Everything already written is
              untouched.{' '}
              {readError instanceof Error ? readError.message : ''}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 rounded-button bg-danger-solid px-3 py-[6px] text-[11.5px] font-semibold leading-none text-on-dark transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
            >
              Try again
            </button>
          </div>
        ) : (
          <textarea
            // Changes exactly twice: on a new DATE, and the one time a restored draft is put
            // back. Never again after that — see the undo contract above.
            key={draft.fieldKey}
            defaultValue={restored?.text ?? entry?.text ?? ''}
            onChange={(e) => onType(e.target.value)}
            disabled={isPending}
            placeholder="Write as much or as little as you want. Nothing here is graded."
            aria-label="Today's journal entry"
            // FULL WIDTH, AND SIX LINES BEFORE IT SCROLLS (owner, 2026-08-29).
            //
            // It used to be capped at `maxWidth: 960px` with `min-h-[96px]`, which on a wide
            // monitor left a dead gap between the last word and the voice-memo rule and cut the
            // entry off mid-sentence after three and a half lines. The cap is gone: the box now
            // runs to the end of the column, which is where the voice-memo divider already stops
            // it. 168px is six lines at this 28.38px line-height, and `flex-1` still lets the box
            // absorb any extra height the voice-memo column forces on the card — it is a floor,
            // not a fixed height. The handoff's own layout note asks for exactly this: the
            // composer "owns the vertical space instead of a dead lower half".
            className="mt-[14px] min-h-[168px] w-full flex-1 resize-none border-0 bg-transparent p-0 text-[16.5px] leading-[1.72] text-ink-2 outline-none placeholder:font-normal placeholder:text-microcopy text-pretty"
          />
        )}

        {/* ONE FOOTER ROW, NOT THREE (owner, 2026-08-29). The word count and the tags used to sit
            on their own row above a stacked Mood/Energy pair, which spent three lines of the card
            on things that each occupy a fraction of its width — and every one of those lines came
            straight off the writing area. They now share a single row: chips first, then the two
            dot rows in the space that was empty anyway. `flex-wrap` keeps it honest on a narrow
            window, where it falls back to the stacked arrangement instead of overflowing. */}
        <div className="mt-4 flex flex-none flex-wrap items-center gap-x-7 gap-y-3">
          <div className="flex flex-wrap items-center gap-[9px]">
            <Chip>
              {words} {words === 1 ? 'word' : 'words'}
            </Chip>
            {entry?.tags.map((tag) => <Chip key={tag}>#{tag}</Chip>)}
            {/* LIVE. It opens a small field, and Enter appends the tag to the entry's stored
                `tags` and saves it through this same PUT — see components/journal/AddTagChip.tsx
                for why a tag is a field and not a `#` typed into the prose. Held back until the
                read lands: appending to `[]` before the entry has arrived would drop the tags the
                entry already has. */}
            {entry && <AddTagChip date={date} tags={entry.tags} />}
          </div>

          {/* Hidden, not deleted (Settings → Journal defaults). A mood already on the entry is
              still on the entry, still in the API, and comes straight back when the rows return. */}
          {prefs.moodEnergy && (
            <div className="flex flex-wrap items-center gap-x-7 gap-y-[7px]">
              <DotRow
                label="Mood"
                value={entry?.mood ?? null}
                onPick={(v) => save.mutate({ date, mood: v })}
              />
              <DotRow
                label="Energy"
                value={entry?.energy ?? null}
                onPick={(v) => save.mutate({ date, energy: v })}
              />
            </div>
          )}
        </div>
      </div>

      <VoiceMemoColumn date={date} />
    </section>
  )
}

// ── The eyebrow row ─────────────────────────────────────────────────────────

/**
 * The chip never claims more than it knows. At rest it reports when the entry was last written
 * from the entry's own `updatedAt` — "Saved just now" only inside five minutes, the clock time
 * otherwise, and nothing at all for a day that has never been written.
 */
function SaveChip({
  state,
  unconfirmed,
  kept,
  autoRetries,
  lastSavedAt,
  date,
  nowMinutes,
}: {
  state: SaveState
  /**
   * A draft is on this machine that the server has not confirmed. It OUTRANKS both 'saved' and
   * the idle stamp: `lastSavedAt` is `entry.updatedAt`, which the optimistic write sets BEFORE
   * the request, so the chip could otherwise say "Saved just now" over words the server never
   * accepted. The draft store is the only thing that knows, so it wins.
   */
  unconfirmed: boolean
  /** The draft write actually landed. False in a browser that refuses storage. */
  kept: boolean
  /** Something will send these words without the owner touching anything. */
  autoRetries: boolean
  lastSavedAt: string | null
  date: ISODate
  nowMinutes: number
}) {
  // ONE SENTENCE FOR EVERY WAY THE WORDS ARE NOT SAVED, promising only what is wired: the retry
  // line appears exactly when the `online`/visible resend would fire, and this browser is only
  // credited with keeping the words when it did.
  if (unconfirmed || state === 'failed') {
    if (state === 'saving') {
      return (
        <span
          aria-live="polite"
          className="rounded-pill bg-fill px-2 py-[3px] text-[10.5px] font-semibold leading-[1.4] text-muted"
        >
          Saving…
        </span>
      )
    }
    return (
      <span
        aria-live="polite"
        className="rounded-pill bg-danger-fill px-2 py-[3px] text-[10.5px] font-semibold leading-[1.4] text-danger-2"
      >
        {unsavedSentence(kept, autoRetries, 'in this browser')}
      </span>
    )
  }

  if (state === 'idle') {
    if (!lastSavedAt) return null
    const sameDay = lastSavedAt.slice(0, 10) === date
    const savedMinutes = Number(lastSavedAt.slice(11, 13)) * 60 + Number(lastSavedAt.slice(14, 16))
    const justNow = sameDay && nowMinutes - savedMinutes >= 0 && nowMinutes - savedMinutes <= 5
    return (
      <span className="rounded-pill bg-success-fill px-2 py-[3px] text-[10.5px] font-semibold leading-[1.4] text-success-dot">
        {justNow ? 'Saved just now' : `Saved ${clockFromTimestamp(lastSavedAt)}`}
      </span>
    )
  }

  const tone = state === 'saved' ? 'bg-success-fill text-success-dot' : 'bg-fill text-muted'
  return (
    <span
      aria-live="polite"
      className={`rounded-pill px-2 py-[3px] text-[10.5px] font-semibold leading-[1.4] ${tone}`}
    >
      {state === 'saving' && 'Saving…'}
      {state === 'saved' && 'Saved just now'}
    </span>
  )
}

/**
 * The "why this prompt" glyph. The prompt is a suggestion and never a required field, and this
 * is where that is said out loud — hover or focus, one sentence, no dialog.
 */
function WhyThisPrompt() {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative flex items-center">
      <button
        type="button"
        aria-label="Why this prompt?"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="flex items-center text-faintest transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-muted"
      >
        <Info size={13} strokeWidth={1.8} />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-[22px] z-10 w-[260px] rounded-row border border-line bg-surface px-3 py-[9px] text-[11px] leading-[1.5] text-ink-3 shadow-card"
        >
          The same prompt every year on this date — picked from the date itself, never from what
          you wrote. Ignore it and write anything; it is a suggestion, not a field.
        </span>
      )}
    </span>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-pill border border-line-3 bg-surface-4 px-3 py-[6px] text-[11.5px] font-medium leading-[1.3] text-ink-2">
      {children}
    </span>
  )
}

/**
 * One-tap 1–5, as five dots. Tapping the current value again clears it: skipping has to be as
 * easy as setting, and an unset row is deliberately almost invisible — the mockup draws it as
 * five hollow rings, so a blank day reads as blank rather than as an unanswered question.
 */
function DotRow({
  label,
  value,
  onPick,
}: {
  label: string
  value: number | null
  onPick: (v: number | null) => void
}) {
  return (
    <div className="flex items-center gap-[9px]">
      <span className="w-[46px] flex-none text-[10.5px] font-medium leading-none tracking-[0.04em] text-faint">
        {label}
      </span>
      <div className="flex items-center gap-[5px]" role="group" aria-label={`${label}, 1 to 5`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = value !== null && n <= value
          return (
            <button
              key={n}
              type="button"
              aria-pressed={filled}
              aria-label={`${label} ${n}`}
              onClick={() => onPick(value === n ? null : n)}
              className={[
                'size-[9px] rounded-full transition-colors',
                'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                filled ? 'bg-faint' : 'border border-line-2 hover:border-faint',
              ].join(' ')}
            />
          )
        })}
      </div>
      {value === null && (
        <span className="text-[10px] leading-none text-faintest">skippable</span>
      )}
    </div>
  )
}

// ── The voice-memo column ───────────────────────────────────────────────────

function VoiceMemoColumn({ date }: { date: ISODate }) {
  const { data: memos, isPending, error } = useVoiceMemos(date)
  const week = { from: startOfWeek(date), to: date }
  const { data: weekEntries, error: weekError } = useJournalEntries(week)

  return (
    <aside className="flex w-[176px] flex-none flex-col gap-3 border-l border-line-4 pl-6">
      <span className="text-[10.5px] font-semibold leading-none tracking-[0.07em] text-muted">
        VOICE MEMO
      </span>

      {/* THE RECORD BUTTON IS LIVE (V1). It records in the browser, uploads through the Worker to
          R2, and the desktop worker transcribes it — see VoiceRecorder.tsx for the whole path.
          In demo mode it stays inert and says why: there is no R2 and no GPU behind a fixture. */}
      <VoiceRecorder date={date} />

      <div className="flex flex-col gap-2">
        {isPending && <span className="text-[10.5px] leading-[1.35] text-faint">…</span>}
        {error && (
          <p className="text-[10.5px] leading-[1.4] text-microcopy text-pretty">
            {error instanceof Error ? error.message : 'Memos could not be loaded.'}
          </p>
        )}
        {memos?.length === 0 && (
          <p className="text-[10.5px] leading-[1.35] text-faint">No memos on this entry.</p>
        )}
        {memos?.map((memo) => <MemoRow key={memo.id} memo={memo} />)}
      </div>

      {/* REAL SINCE V6. The count is the archive's own range read, scoped to Monday-through-today,
          so this number and the streak line on the Journal screen cannot disagree. When the read
          fails the chip shows an em dash and carries the reason in its title — a zero here would
          claim the owner wrote nothing this week, which is a different and worse thing to say. */}
      <div className="mt-auto flex items-center justify-between gap-2 rounded-row bg-surface-4 px-3 py-[10px]">
        <span className="text-[11.5px] font-medium leading-none text-ink-2">Entries this week</span>
        {weekError ? (
          <span
            title={
              weekError instanceof Error
                ? `${weekError.message} The count is not shown rather than shown as zero.`
                : undefined
            }
            className="text-[13px] font-bold leading-none text-faint"
          >
            —
          </span>
        ) : (
          <span className="text-[13px] font-bold leading-none">{weekEntries?.length ?? '…'}</span>
        )}
      </div>
    </aside>
  )
}

/**
 * recording → queued → transcribed, plus `failed`. A queued memo names the desktop worker as what
 * it waits on; a failed one carries the worker's own reason, because "it did not work" with no
 * cause is indistinguishable from "it is still working".
 *
 * THE TRANSCRIPT TEXT IS NOT IN THIS ROW, and that is the mockup's call, not an omission: the
 * column is 176px wide and shows a stamp and a state (option 3c). The words themselves are
 * rendered in full on the day page, where option 5b puts them inline in the entry. They are on
 * this row's `title` so the column is not the only place they exist.
 */
function MemoRow({ memo }: { memo: JournalVoiceMemo }) {
  const stamp = `${clockFromTimestamp(memo.recordedAt)} · ${durationPhrase(memo.durationSec)}`
  const transcribed = memo.status === 'transcribed'
  const failed = memo.status === 'failed'
  return (
    <div className="flex items-start gap-2" title={memo.transcriptText ?? memo.error ?? undefined}>
      {transcribed && <Check size={12} className="mt-[2px] flex-none text-success-dot" />}
      {memo.status === 'queued' && (
        <span
          className="mt-[2px] size-[12px] flex-none rounded-full border-[1.6px] border-line-2 border-t-microcopy"
          aria-hidden
        />
      )}
      {memo.status === 'recording' && (
        <span className="mt-[2px] size-[12px] flex-none rounded-full bg-danger-dot" aria-hidden />
      )}
      {failed && (
        <span
          className="mt-[2px] size-[12px] flex-none rounded-full border-[1.6px] border-danger-dot"
          aria-hidden
        />
      )}
      <div className="min-w-0">
        <div
          className={[
            'text-[11px] font-semibold leading-[1.3]',
            transcribed ? 'text-ink-2' : failed ? 'text-danger-2' : 'text-muted',
          ].join(' ')}
        >
          {stamp}
        </div>
        <div
          className={[
            'mt-[2px] text-[10.5px] leading-[1.35] text-pretty',
            transcribed ? 'text-success-dot' : failed ? 'text-danger' : 'text-faint',
          ].join(' ')}
        >
          {transcribed && 'transcribed into this entry'}
          {memo.status === 'queued' && 'waiting for desktop worker'}
          {memo.status === 'recording' && 'recording now'}
          {/* The audio is safe on the PC either way — that is the sentence that decides whether
              this is a shrug or a panic, so it is said here rather than left to be inferred. */}
          {failed && (memo.error ?? 'the desktop worker could not transcribe this one')}
        </div>
      </div>
    </div>
  )
}
